import React, { useCallback, useMemo } from "react";
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import { Box, Text } from "@mantine/core";

const libraries: ("places")[] = ["places"];

interface GoogleMapPickerProps {
  latitude: number;
  longitude: number;
  onLocationChange: (lat: number, lng: number) => void;
}

const GoogleMapPicker: React.FC<GoogleMapPickerProps> = ({
  latitude,
  longitude,
  onLocationChange,
}) => {
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey,
    libraries,
  });

  const center = useMemo(
    () => ({
      lat: latitude && latitude !== 0 ? latitude : 40.7128, // Default to New York
      lng: longitude && longitude !== 0 ? longitude : -74.0060, // Default to New York
    }),
    [latitude, longitude]
  );

  const mapOptions = useMemo(
    () => ({
      disableDefaultUI: false,
      clickableIcons: false,
      scrollwheel: true,
      zoomControl: true,
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: true,
    }),
    []
  );

  const onMarkerDragEnd = useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (e.latLng) {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        onLocationChange(lat, lng);
      }
    },
    [onLocationChange]
  );

  const onMapClick = useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (e.latLng) {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        onLocationChange(lat, lng);
      }
    },
    [onLocationChange]
  );

  if (loadError) {
    return (
      <Box
        style={{
          height: "400px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1.5px solid #d0d5dd",
          borderRadius: "6px",
          backgroundColor: "#f9fafb",
        }}
      >
        <Text c="dimmed">Error loading Google Maps. Please check your API key.</Text>
      </Box>
    );
  }

  if (!isLoaded) {
    return (
      <Box
        style={{
          height: "400px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1.5px solid #d0d5dd",
          borderRadius: "6px",
          backgroundColor: "#f9fafb",
        }}
      >
        <Text c="dimmed">Loading map...</Text>
      </Box>
    );
  }

  return (
    <Box
      style={{
        height: "400px",
        width: "100%",
        border: "1.5px solid #d0d5dd",
        borderRadius: "6px",
        overflow: "hidden",
      }}
    >
      <GoogleMap
        zoom={latitude && longitude && latitude !== 0 && longitude !== 0 ? 15 : 2}
        center={center}
        mapContainerStyle={{ width: "100%", height: "100%" }}
        options={mapOptions}
        onClick={onMapClick}
      >
        {(latitude !== 0 || longitude !== 0) ? (
          <Marker
            position={{ lat: latitude, lng: longitude }}
            draggable={true}
            onDragEnd={onMarkerDragEnd}
          />
        ) : null}
      </GoogleMap>
    </Box>
  );
};

export default GoogleMapPicker;

